import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, unref as _unref, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-section-title__content" }
const _hoisted_3 = { class: "c-section-title__inner" }
const _hoisted_4 = {
  key: 0,
  class: "c-section-title__heading"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "c-section-title__body" }
const _hoisted_7 = {
  key: 0,
  class: "c-section-title__divider",
  "aria-hidden": "true"
}
const _hoisted_8 = {
  key: 2,
  class: "c-section-title__actions"
}
const _hoisted_9 = {
  key: 0,
  class: "c-section-title__nested"
}

import { computed } from 'vue';
import { Placeholder, RichText } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import type { SectionTitleProps } from '@/types/components/SectionTitle';
import type { ScItem } from '@/types/sitecore';
import type { SiteButtonFields } from '@/types/elements/site-button';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import componentFactory from '@/temp/componentFactory';
import utils from '@/utils';

type DynamicComponentItem = ComponentRendering & {
  id?: string;
  template?: string;
  templateName?: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'SectionTitle',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const layout = computed(() => fields.value?.layout?.fields?.phrase?.value || 'split');
const isChecked = (value: unknown) =>
  value === true || value === 1 || value === '1' || value === 'true';
const titleTag = computed(() => (isChecked(fields.value?.isH1?.value) ? 'h1' : 'h2'));
const showDivider = computed(() => Boolean(fields.value?.showDivider?.value));
const hasHorizontalMargin = computed(() => Boolean(fields.value?.hasHorizontalMargin?.value));
const buttons = computed<ScItem<SiteButtonFields>[]>(() => {
  const buttonItems = utils.sc.getFieldObj(fields.value, 'buttons');
  return Array.isArray(buttonItems) ? buttonItems : [];
});
const nestedComponents = computed<DynamicComponentItem[]>(() => {
  const components = utils.sc.getFieldObj(fields.value, 'components');
  return Array.isArray(components)
    ? components.map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      }))
    : [];
});
const placeholderRenderings = computed<ComponentRendering[]>(() => {
  const placeholders = rendering.value?.placeholders as Record<string, ComponentRendering[]>;
  const renderings = placeholders?.['jss-section'];
  return Array.isArray(renderings) ? renderings : [];
});
const hasPlaceholderContent = computed(() => placeholderRenderings.value.length > 0);

const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
}));

const rootClass = computed(() => [
  utils.overall.baseComClass(props.fields, 'c-section-title'),
  `c-section-title--${layout.value}`,
  {
    'c-section-title--with-divider': showDivider.value,
    'c-section-title--with-actions': buttons.value.length > 0,
    'c-section-title--with-background': Boolean(fields.value?.backgroundImage),
    'c-section-title--with-horizontal-margin': hasHorizontalMargin.value,
  },
]);

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'ImageTextCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
  };
}

return (_ctx: any,_cache: any) => {
  const _component_SiteButton = _resolveComponent("SiteButton")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: rendering.value?.dataSource
      }, [
        (fields.value.backgroundImage)
          ? (_openBlock(), _createBlock(AdaptiveImage, {
              key: 0,
              class: "c-section-title__background-image",
              field: fields.value.backgroundImage,
              "is-background": false,
              show: true,
              "enable-scroll-detection": false,
              "show-image-redius": false
            }, null, 8, ["field"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_ContentGrid, {
            "desktop-default-grid": [2, 20],
            "mobile-default-grid": [1, 14]
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                (fields.value?.title.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(titleTag.value), { class: "c-section-title__title" }, {
                        default: _withCtx(() => [
                          _createElementVNode("strong", {
                            innerHTML: fields.value?.title.value
                          }, null, 8, _hoisted_5)
                        ]),
                        _: 1
                      }))
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_6, [
                  (showDivider.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_7))
                    : _createCommentVNode("", true),
                  (fields.value.description?.value)
                    ? (_openBlock(), _createBlock(_unref(RichText), {
                        key: 1,
                        class: "c-section-title__description",
                        field: fields.value.description
                      }, null, 8, ["field"]))
                    : _createCommentVNode("", true),
                  (buttons.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buttons.value, (button, index) => {
                          return (_openBlock(), _createBlock(_component_SiteButton, _mergeProps({
                            key: button.id || index
                          }, { ref_for: true }, button), null, 16))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ]),
            _: 1
          }),
          (hasPlaceholderContent.value || nestedComponents.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                (hasPlaceholderContent.value)
                  ? (_openBlock(), _createBlock(_unref(Placeholder), {
                      key: 0,
                      name: "jss-section",
                      rendering: rendering.value
                    }, null, 8, ["rendering"]))
                  : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(nestedComponents.value, (componentData, componentIndex) => {
                      return (_openBlock(), _createBlock(_resolveDynamicComponent(resolveDynamicComponent(componentData)), _mergeProps({ ref_for: true }, getDynamicComponentProps(componentData), {
                        key: componentData?.uid ?? componentData?.id ?? `section-title-${componentIndex}`
                      }), null, 16))
                    }), 128))
              ]))
            : _createCommentVNode("", true)
        ])
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})